import { IconGoogleBusiness, IconGoogleSearch } from '../icons';
// =============================================================
// GoogleBusinessProfileCard  Phase 4
// Google Business Profile Connection, Location Mapping, and Performance
// =============================================================

import React, { useState } from 'react';
import {
  GoogleBusinessConnectionDto,
  GoogleBusinessLocationOption,
  GoogleBusinessSummaryDto,
} from '../../lib/api';
import { ConnectionStatus } from './ConnectionStatus';
import { SyncButton } from './SyncButton';
import { LocationSelector } from './LocationSelector';

interface Props {
  googleConnected: boolean;
  connectedLocation: GoogleBusinessConnectionDto | null;
  availableLocations?: GoogleBusinessLocationOption[];
  summary: GoogleBusinessSummaryDto | null;
  onSelectLocation: (data: { locationId: string; locationName: string; accountId?: string }) => Promise<void>;
  onSync: () => Promise<void>;
  onFetchLocations?: () => Promise<void>;
  loadingLocations: boolean;
  errorLocations?: string | null;
  syncing: boolean;
  onConnectGoogle: () => void;
}

export const GoogleBusinessProfileCard: React.FC<Props> = ({
  googleConnected,
  connectedLocation,
  availableLocations = [],
  summary,
  onSelectLocation,
  onSync,
  onFetchLocations,
  loadingLocations,
  errorLocations,
  syncing,
  onConnectGoogle,
}) => {
  const [showSelector, setShowSelector] = useState(false);
  const initializedRef = React.useRef(false);

  React.useEffect(() => {
    if (!initializedRef.current && googleConnected) {
      if (!connectedLocation || connectedLocation.status !== 'CONNECTED') {
        setShowSelector(true);
        if (onFetchLocations) {
          onFetchLocations();
        }
      }
      initializedRef.current = true;
    }
  }, [connectedLocation, googleConnected, onFetchLocations]);

  const handleToggleSelector = async () => {
    const nextState = !showSelector;
    setShowSelector(nextState);
    if (nextState && onFetchLocations) {
      await onFetchLocations();
    }
  };

  const getStatus = () => {
    if (!googleConnected) return 'NOT_CONNECTED';
    if (!connectedLocation || connectedLocation.status !== 'CONNECTED') return 'DISCONNECTED';
    return 'CONNECTED';
  };

  const getStatusLabel = () => {
    if (!googleConnected) return 'Google Not Connected';
    if (!connectedLocation || connectedLocation.status !== 'CONNECTED') return 'Location Not Selected';
    return 'Location Connected';
  };

  return (
    <div
      className="card integration-dashboard-card"
      style={{
        padding: '24px',
        background: '#fff',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      }}
    >
      {/* Top Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '12px',
          marginBottom: '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              background: '#f0fdf4',
              border: '1px solid #dcfce7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.6rem',
            }}
          >
            <IconGoogleBusiness style={{ width: 24, height: 24, color: '#10b981' }} />
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#0f172a' }}>
                Google Business Profile
              </h3>
              <span
                style={{
                  fontSize: '0.72rem',
                  padding: '2px 8px',
                  background: '#dcfce7',
                  color: '#166534',
                  borderRadius: '4px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Data Source: Google Business Profile
              </span>
            </div>
            <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
              Google Maps & Local Pack visibility, direct calls, directions, and customer actions
            </span>
          </div>
        </div>

        <ConnectionStatus status={getStatus()} label={getStatusLabel()} />
      </div>

      {!googleConnected ? (
        <div
          style={{
            padding: '20px',
            background: '#f8fafc',
            borderRadius: '8px',
            border: '1px dashed #cbd5e1',
            textAlign: 'center',
          }}
        >
          <p style={{ margin: '0 0 12px', fontSize: '0.9rem', color: '#475569' }}>
            Connect your Google account above to discover and select your Google Business Profile location.
          </p>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={onConnectGoogle}
          >
            Connect Google Account First 
          </button>
        </div>
      ) : (
        <>
          {/* Location status & selector toggle */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '12px',
              marginBottom: '16px',
              padding: '12px 16px',
              background: '#f8fafc',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
            }}
          >
            <div>
              <span style={{ fontSize: '0.78rem', color: '#64748b', display: 'block' }}>Connected Location</span>
              <strong style={{ fontSize: '0.95rem', color: '#0f172a' }}>
                {connectedLocation?.googleLocationName ? connectedLocation.googleLocationName : 'None selected'}
              </strong>
              {connectedLocation?.googleLocationId && (
                <span style={{ marginLeft: '8px', fontSize: '0.75rem', color: '#059669' }}>
                  (ID: {connectedLocation.googleLocationId})
                </span>
              )}
            </div>

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                type="button"
                id="gbp-select-location-btn"
                className="btn btn-secondary btn-sm"
                onClick={handleToggleSelector}
              >
                {showSelector ? 'Close Selector' : connectedLocation ? 'Change Location' : 'Select Location'}
              </button>

              {connectedLocation && (
                <SyncButton
                  onSync={onSync}
                  loading={syncing}
                  lastSyncedAt={connectedLocation.lastSyncedAt}
                  label="Sync Performance Data (28d)"
                  sourceLabel="GBP"
                />
              )}
            </div>
          </div>

          {/* Collapsible Location Selector */}
          {showSelector && (
            <div style={{ marginTop: '16px', marginBottom: '20px' }}>
              <LocationSelector
                connectedLocation={connectedLocation}
                availableLocations={availableLocations || []}
                onSelectLocation={async (data) => {
                  await onSelectLocation(data);
                  setShowSelector(false);
                }}
                onRetry={onFetchLocations}
                loading={loadingLocations}
                error={errorLocations}
              />
            </div>
          )}

          {/* Metrics Display */}
          {connectedLocation && (
            <div style={{ marginTop: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 600, color: '#1e293b' }}>
                  Business Profile Performance (Past 28 Days)
                </h4>
                {summary && (
                  <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                    Period: {summary.dateRange.start} to {summary.dateRange.end}
                  </span>
                )}
              </div>

              {!summary || summary.totalImpressions === 0 ? (
                <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '8px', textAlign: 'center', color: '#64748b', fontSize: '0.88rem' }}>
                  No performance metrics synced yet. Click <strong>"Sync Performance Data (28d)"</strong> to import customer actions, calls, and map impressions.
                </div>
              ) : (
                <>
                  {/* Grid of GBP Performance Metrics */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                    <div style={{ padding: '14px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                      <span style={{ fontSize: '0.72rem', color: '#166534', fontWeight: 600, display: 'block' }}>SEARCH VIEWS</span>
                      <strong style={{ fontSize: '1.4rem', color: '#15803d' }}>{summary.searchImpressions.toLocaleString()}</strong>
                      <span style={{ fontSize: '0.7rem', color: '#166534', display: 'block', marginTop: '2px' }}>Google Search</span>
                    </div>

                    <div style={{ padding: '14px', background: '#ecfdf5', borderRadius: '8px', border: '1px solid #a7f3d0' }}>
                      <span style={{ fontSize: '0.72rem', color: '#047857', fontWeight: 600, display: 'block' }}>MAPS VIEWS</span>
                      <strong style={{ fontSize: '1.4rem', color: '#059669' }}>{summary.mapsImpressions.toLocaleString()}</strong>
                      <span style={{ fontSize: '0.7rem', color: '#047857', display: 'block', marginTop: '2px' }}>Google Maps App</span>
                    </div>

                    <div style={{ padding: '14px', background: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                      <span style={{ fontSize: '0.72rem', color: '#1e40af', fontWeight: 600, display: 'block' }}>CALL CLICKS</span>
                      <strong style={{ fontSize: '1.4rem', color: '#1d4ed8' }}>{summary.callClicks.toLocaleString()}</strong>
                      <span style={{ fontSize: '0.7rem', color: '#1e40af', display: 'block', marginTop: '2px' }}>Direct Phone Leads</span>
                    </div>

                    <div style={{ padding: '14px', background: '#f5f3ff', borderRadius: '8px', border: '1px solid #ddd6fe' }}>
                      <span style={{ fontSize: '0.72rem', color: '#6d28d9', fontWeight: 600, display: 'block' }}>WEBSITE CLICKS</span>
                      <strong style={{ fontSize: '1.4rem', color: '#7c3aed' }}>{summary.websiteClicks.toLocaleString()}</strong>
                      <span style={{ fontSize: '0.7rem', color: '#6d28d9', display: 'block', marginTop: '2px' }}>Visits from Listing</span>
                    </div>

                    <div style={{ padding: '14px', background: '#fffbeb', borderRadius: '8px', border: '1px solid #fde68a' }}>
                      <span style={{ fontSize: '0.72rem', color: '#92400e', fontWeight: 600, display: 'block' }}>DIRECTIONS</span>
                      <strong style={{ fontSize: '1.4rem', color: '#b45309' }}>{summary.directionRequests.toLocaleString()}</strong>
                      <span style={{ fontSize: '0.7rem', color: '#92400e', display: 'block', marginTop: '2px' }}>Route Requests</span>
                    </div>
                  </div>

                  {/* Top Keywords / Search Queries on GBP */}
                  {summary.topKeywords && summary.topKeywords.length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      <h5 style={{ margin: '0 0 4px', fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>
                        Google Business Profile search keyword impressions
                      </h5>
                      <span style={{ fontSize: '0.72rem', color: '#64748b', display: 'block', marginBottom: '8px' }}>
                        Direct monthly keyword search volume that resulted in your Business Profile appearing on Maps/Search.
                      </span>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid #e2e8f0', color: '#64748b', textAlign: 'left' }}>
                              <th style={{ padding: '6px 8px' }}>Keyword</th>
                              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Monthly Profile Impressions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {summary.topKeywords.slice(0, 5).map((k, idx) => (
                              <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                <td style={{ padding: '6px 8px', fontWeight: 500, color: '#1e293b' }}>{k.keyword}</td>
                                <td style={{ padding: '6px 8px', textAlign: 'right', color: '#15803d', fontWeight: 600 }}>
                                  {k.impressions.toLocaleString()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
